<template>
  <main class="plan-page">
    <AppHeader />
    <div class="hub-shell" aria-labelledby="plan-title">
      <!-- 1. 旅行中の現在の予定と次の予定（アクティブな旅行がある場合） -->
      <section v-if="activeTrip" class="home-live-section" aria-labelledby="live-section-title">
        <div class="live-section-header">
          <div class="live-status-pill">
            <span class="live-dot"></span>
            <span>{{ isOngoingTrip ? '旅行中 LIVE' : '直近の旅行予定' }}</span>
          </div>
          <router-link :to="`/shiori/${activeTrip.SHIORI_NOV}`" class="live-shiori-link">
            <span>{{ activeTrip.SUBJECT_NMV || '旅行のしおり' }}</span>
            <IconSvg name="arrow-right-from-bracket-solid-full" size="xs" />
          </router-link>
        </div>

        <div class="live-schedule-card">
          <div class="live-schedule-grid">
            <!-- 現在の予定 -->
            <div class="live-schedule-block current-block">
              <span class="block-badge is-current">いまの予定</span>
              <h3 class="schedule-spot-title">{{ currentScheduleText }}</h3>
              <p class="schedule-spot-meta">{{ currentScheduleMeta }}</p>
            </div>

            <div class="live-divider"></div>

            <!-- 次の予定 -->
            <div class="live-schedule-block next-block">
              <span class="block-badge is-next">つぎの予定</span>
              <h3 class="schedule-spot-title">{{ nextScheduleText }}</h3>
              <p class="schedule-spot-meta">{{ nextScheduleMeta }}</p>
            </div>
          </div>

          <div class="live-card-footer">
            <router-link :to="`/schedule?shioriId=${activeTrip.SHIORI_NOV}`" class="btn btn-secondary btn-sm">
              <IconSvg name="calendar-solid-full" size="xs" />
              <span>全体のスケジュールを見る</span>
            </router-link>
          </div>
        </div>
      </section>

      <!-- 2. 全体のフローを示す図（HOMEでは全項目を均等表示） -->
      <TripWorkflowDiagram :current-step="null" />

      <!-- 3. メインアクションカード -->
      <div class="intro-panel">
        <p class="eyebrow">HOME / 旅行ハブ</p>
        <h1 id="plan-title">どこから始めますか？</h1>
        <p class="lead">
          旅行の検討から計画、旅行中のスケジュール確認、旅行後の評価・振り返りまで。今の目的に合わせて進めてください。
        </p>
      </div>

      <div class="action-grid" aria-label="旅行の主要アクション">
        <router-link
          v-for="action in actions"
          :key="action.to"
          :to="action.to"
          class="action-panel"
          :aria-label="action.label"
        >
          <span class="action-icon" aria-hidden="true">
            <!-- 旅先検討アイコン -->
            <svg v-if="action.iconType === 'globe'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="action-svg">
              <circle cx="12" cy="12" r="10"/>
              <line x1="2" y1="12" x2="22" y2="12"/>
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
            </svg>
            <!-- 旅程管理アイコン -->
            <svg v-else-if="action.iconType === 'shiori'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="action-svg">
              <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
              <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
              <path d="M9 12h6"/>
              <path d="M9 16h6"/>
            </svg>
            <!-- 評価アイコン -->
            <svg v-else-if="action.iconType === 'star'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="action-svg">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
            </svg>
          </span>
          <span class="action-copy">
            <strong>{{ action.label }}</strong>
            <small>{{ action.description }}</small>
          </span>
          <IconSvg name="arrow-right-from-bracket-solid-full" size="sm" class="action-arrow" />
        </router-link>
      </div>

      <!-- 4. 最近の旅行セクション（添付写真準拠） -->
      <section v-if="recentTrips.length > 0" class="recent-trips-section" aria-labelledby="recent-title">
        <div class="recent-header">
          <h2 id="recent-title" class="recent-title">最近の旅行</h2>
          <router-link to="/mypage" class="recent-more-link">すべて見る</router-link>
        </div>
        <div class="recent-trips-list">
          <router-link
            v-for="trip in recentTrips"
            :key="trip.SHIORI_NOV"
            :to="`/shiori/${trip.SHIORI_NOV}`"
            class="recent-trip-card"
          >
            <div class="recent-trip-thumb">
              <img v-if="trip.IMAGE_URL" :src="trip.IMAGE_URL" alt="" />
              <div v-else class="thumb-placeholder">
                <IconSvg name="plane-solid-full" size="sm" />
              </div>
            </div>
            <div class="recent-trip-info">
              <strong class="recent-trip-title">{{ trip.SUBJECT_NMV || '旅行のしおり' }}</strong>
              <div class="recent-trip-meta">
                <span class="trip-status-tag">計画中</span>
                <span class="trip-meta-date">{{ formatDateRange(trip.TRIP_FROM, trip.TRIP_TO) }}</span>
                <span class="trip-meta-members">メンバー {{ trip.members ? trip.members.length : 1 }}人</span>
              </div>
            </div>
            <div class="recent-trip-menu-icon" aria-hidden="true">
              <span>⋮</span>
            </div>
          </router-link>
        </div>
      </section>

      <!-- 5. おすすめの進め方フロー -->
      <section class="flow-panel" aria-labelledby="flow-title">
        <h2 id="flow-title">おすすめの進め方</h2>
        <ol class="flow-list">
          <li>
            <span class="step-number">1</span>
            <div>
              <strong>旅先検討</strong>
              <p>参加メンバーで候補地を出し合い、投票やAI旅行相談でどこに行くか決めます。</p>
            </div>
          </li>
          <li>
            <span class="step-number">2</span>
            <div>
              <strong>旅程管理・作成</strong>
              <p>決まった行き先からしおりを作成し、日程、スケジュール、持ち物、ToDoを整えます。</p>
            </div>
          </li>
          <li>
            <span class="step-number">3</span>
            <div>
              <strong>旅行中</strong>
              <p>現在の予定や次の移動を確認しながら旅行を楽しみ、旅費の割り勘をその場で記録します。</p>
            </div>
          </li>
          <li>
            <span class="step-number">4</span>
            <div>
              <strong>評価・振り返り</strong>
              <p>旅行後に評価を記録すると、メンバーの好みが蓄積され、次の旅行先が自動提案されます。</p>
            </div>
          </li>
        </ol>
      </section>
    </div>
  </main>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import AppHeader from "@/components/AppHeader.vue";
import IconSvg from "@/components/IconSvg.vue";
import TripWorkflowDiagram from "@/components/TripWorkflowDiagram.vue";
import { useShioriStore } from "@/store/shiori-store.js";
import { useScheduleStore } from "@/store/schedule-store.js";
import { useAuthStore } from "@/store/authStore.js";

const shioriStore = useShioriStore();
const scheduleStore = useScheduleStore();
const authStore = useAuthStore();

const actions = [
  {
    label: "旅先検討（どこに行くか決める）",
    description: "候補ボードやAI相談を使って、みんなの行きたい場所を比較・検討します。",
    to: "/destination-boards",
    iconType: "globe",
  },
  {
    label: "旅程管理（しおり・スケジュール）",
    description: "作成したしおり一覧から、日程、持ち物、割り勘の確認・編集を行います。",
    to: "/mypage",
    iconType: "shiori",
  },
  {
    label: "評価ダッシュボード（振り返りと提案）",
    description: "過去の旅行評価を分析し、好みに合った次の旅行先候補を確認します。",
    to: "/reviews",
    iconType: "star",
  },
];

// 最近の旅行（3件まで）
const recentTrips = computed(() => {
  return (shioriStore.shioris || []).slice(0, 3);
});

const formatDateRange = (from, to) => {
  if (!from) return "日程未設定";
  const f = from.slice(0, 10).replace(/-/g, "/");
  const t = to ? to.slice(0, 10).replace(/-/g, "/") : f;
  return `${f} 〜 ${t}`;
};

// アクティブな旅行（旅行中または直近の旅行）
const activeTrip = ref(null);
const activeSchedules = ref([]);
const isOngoingTrip = ref(false);

const formatDateString = (d) => {
  if (!d) return "";
  const date = new Date(d);
  if (isNaN(date.getTime())) return String(d);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const weekDays = ["日", "月", "火", "水", "木", "金", "土"];
  return `${month}月${day}日(${weekDays[date.getDay()]})`;
};

// 現在の予定のテキスト
const currentScheduleText = computed(() => {
  if (!activeSchedules.value || activeSchedules.value.length === 0) {
    return "予定なし（スケジュールを登録してください）";
  }
  const now = new Date();
  // 現在時刻と重なる予定を探す
  const current = activeSchedules.value.find((s) => {
    if (!s.START_YMD) return false;
    const start = new Date(s.START_YMD + (s.START_TIME ? `T${s.START_TIME}` : "T00:00:00"));
    const end = new Date((s.END_YMD || s.START_YMD) + (s.END_TIME ? `T${s.END_TIME}` : "T23:59:59"));
    return start <= now && now <= end;
  });

  if (current) {
    return current.LINE_TITLE || current.SUBJECT_NMV || "進行中の予定";
  }

  // 進行中の予定がない場合、今日の直近の予定
  const todayUpcoming = activeSchedules.value.find((s) => {
    if (!s.START_YMD) return false;
    const start = new Date(s.START_YMD + (s.START_TIME ? `T${s.START_TIME}` : "T00:00:00"));
    return start > now;
  });

  if (todayUpcoming) {
    return "待機中（次の予定をお待ちください）";
  }

  return activeSchedules.value[0]?.LINE_TITLE || activeSchedules.value[0]?.SUBJECT_NMV || "本日の予定は終了しました";
});

const currentScheduleMeta = computed(() => {
  if (!activeTrip.value) return "";
  return `${formatDateString(activeTrip.value.TRIP_FROM)} 〜 ${formatDateString(activeTrip.value.TRIP_TO)}`;
});

// 次の予定のテキスト
const nextScheduleText = computed(() => {
  if (!activeSchedules.value || activeSchedules.value.length === 0) {
    return "次の予定はありません";
  }
  const now = new Date();
  const next = activeSchedules.value.find((s) => {
    if (!s.START_YMD) return false;
    const start = new Date(s.START_YMD + (s.START_TIME ? `T${s.START_TIME}` : "T00:00:00"));
    return start > now;
  });

  if (next) {
    return next.LINE_TITLE || next.SUBJECT_NMV || "次の予定";
  }

  if (activeSchedules.value.length > 1) {
    return activeSchedules.value[1]?.LINE_TITLE || "次の予定";
  }

  return "次の予定はありません";
});

const nextScheduleMeta = computed(() => {
  const now = new Date();
  const next = activeSchedules.value.find((s) => {
    if (!s.START_YMD) return false;
    const start = new Date(s.START_YMD + (s.START_TIME ? `T${s.START_TIME}` : "T00:00:00"));
    return start > now;
  });

  if (next && next.START_TIME) {
    return `${next.START_TIME.slice(0, 5)} 開始予定`;
  }
  return "目的地へ向かいましょう";
});

onMounted(async () => {
  if (authStore.token) {
    try {
      await shioriStore.fetchShioris();
      const shioris = shioriStore.shioris || [];
      const now = new Date();
      const todayStr = now.toISOString().slice(0, 10);

      // 1. 今日が旅行期間内のしおりを探す
      const ongoing = shioris.find((s) => {
        const from = s.TRIP_FROM?.slice(0, 10);
        const to = s.TRIP_TO?.slice(0, 10);
        return from && to && from <= todayStr && todayStr <= to;
      });

      if (ongoing) {
        activeTrip.value = ongoing;
        isOngoingTrip.value = true;
      } else {
        // 2. 直近の未来の旅行を探す
        const upcoming = shioris
          .filter((s) => s.TRIP_FROM && s.TRIP_FROM.slice(0, 10) >= todayStr)
          .sort((a, b) => (a.TRIP_FROM > b.TRIP_FROM ? 1 : -1))[0];
        
        if (upcoming) {
          activeTrip.value = upcoming;
          isOngoingTrip.value = false;
        } else if (shioris.length > 0) {
          // 直近のしおり
          activeTrip.value = shioris[0];
          isOngoingTrip.value = false;
        }
      }

      // スケジュールの取得
      if (activeTrip.value) {
        try {
          const schedules = await scheduleStore.fetchSchedules(activeTrip.value.SHIORI_NOV);
          activeSchedules.value = schedules || [];
        } catch {
          activeSchedules.value = [];
        }
      }
    } catch {
      activeTrip.value = null;
    }
  }
});
</script>

<style scoped>
.plan-page {
  min-height: 100vh;
  background: var(--color-background);
  color: var(--color-text);
}

.hub-shell {
  display: grid;
  gap: 1.5rem;
  margin: 0 auto;
  max-width: 1040px;
  padding: clamp(24px, 4vw, 40px) clamp(16px, 3vw, 32px);
}

/* ── LIVEセクション ── */
.home-live-section {
  background: var(--color-surface);
  border: 1.5px solid var(--color-primary);
  border-radius: var(--radius-xl);
  padding: 1.25rem 1.5rem;
  box-shadow: var(--shadow-md);
}

.live-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--color-border-light);
}

.live-status-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #dcfce7;
  color: #166534;
  font-size: 0.75rem;
  font-weight: 800;
  padding: 0.25rem 0.75rem;
  border-radius: var(--radius-full);
}

.live-dot {
  width: 8px;
  height: 8px;
  background: #22c55e;
  border-radius: 50%;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.4; transform: scale(1.2); }
}

.live-shiori-link {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  color: var(--color-primary-ink);
  font-weight: 700;
  font-size: 0.875rem;
  text-decoration: none;
}

.live-shiori-link:hover {
  text-decoration: underline;
}

.live-schedule-grid {
  display: flex;
  align-items: stretch;
  gap: 1.25rem;
}

.live-schedule-block {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.block-badge {
  display: inline-block;
  width: fit-content;
  font-size: 0.6875rem;
  font-weight: 800;
  padding: 0.125rem 0.5rem;
  border-radius: var(--radius-full);
}

.block-badge.is-current {
  background: var(--color-primary-light);
  color: var(--color-primary-ink);
}

.block-badge.is-next {
  background: #eff6ff;
  color: #1d4ed8;
}

.schedule-spot-title {
  font-size: 1.125rem;
  font-weight: 800;
  color: var(--color-text);
  margin: 0;
  line-height: 1.3;
}

.schedule-spot-meta {
  font-size: 0.8125rem;
  color: var(--color-text-muted);
  margin: 0;
}

.live-divider {
  width: 1px;
  background: var(--color-border);
  margin: 0.25rem 0;
}

.live-card-footer {
  margin-top: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--color-border-light);
  display: flex;
  justify-content: flex-end;
}

/* ── イントロ & アクション ── */
.intro-panel,
.flow-panel,
.action-panel {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
}

.intro-panel {
  padding: clamp(20px, 4vw, 32px);
}

.eyebrow {
  color: var(--color-primary-ink);
  font-size: 0.82rem;
  font-weight: 800;
  letter-spacing: 0;
  margin: 0 0 8px;
}

h1,
h2,
p {
  margin: 0;
}

h1 {
  font-size: clamp(1.7rem, 4vw, 2.35rem);
  line-height: 1.25;
}

.lead {
  color: var(--color-text-muted);
  line-height: 1.75;
  margin-top: 14px;
  max-width: 760px;
}

.action-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.action-panel {
  color: var(--color-text);
  min-height: 132px;
  padding: 18px;
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  transition: border-color var(--transition-base), box-shadow var(--transition-base), transform var(--transition-base);
}

.action-panel:hover,
.action-panel:focus-visible {
  border-color: var(--color-primary-ink);
  box-shadow: var(--shadow-md);
  transform: translateY(-2px);
}

.action-icon {
  align-items: center;
  background: var(--color-primary-light);
  border-radius: var(--radius-md);
  color: var(--color-primary-ink);
  display: inline-flex;
  flex: 0 0 44px;
  height: 44px;
  justify-content: center;
  width: 44px;
}

.action-svg {
  width: 22px;
  height: 22px;
  color: var(--color-primary-ink);
}

.action-copy {
  display: grid;
  flex: 1;
  gap: 6px;
}

.action-copy strong {
  font-size: 0.9375rem;
  font-weight: 700;
}

.action-copy small {
  color: var(--color-text-muted);
  font-size: 0.8125rem;
  line-height: 1.5;
}

.flow-panel {
  padding: 22px;
}

.flow-panel h2 {
  font-size: 1.1rem;
  margin-bottom: 16px;
  font-weight: 800;
}

.flow-list {
  display: grid;
  gap: 14px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.flow-list li {
  align-items: flex-start;
  display: flex;
  gap: 12px;
}

.step-number {
  align-items: center;
  background: var(--color-navy);
  border-radius: 999px;
  color: #ffffff;
  display: inline-flex;
  flex: 0 0 28px;
  font-weight: 800;
  height: 28px;
  justify-content: center;
  width: 28px;
}

.flow-list p {
  color: var(--color-text-muted);
  line-height: 1.6;
  margin-top: 4px;
  font-size: 0.875rem;
}

/* ── 最近の旅行セクション ── */
.recent-trips-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.recent-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.recent-title {
  font-size: 1.0625rem;
  font-weight: 800;
  color: var(--color-text);
  margin: 0;
}

.recent-more-link {
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--color-primary-ink);
  text-decoration: none;
}

.recent-trips-list {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.recent-trip-card {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: 0.75rem 1rem;
  text-decoration: none;
  color: var(--color-text);
  box-shadow: var(--shadow-sm);
  transition: all var(--transition-fast);
}

.recent-trip-card:hover {
  border-color: var(--color-primary-ink);
  transform: translateY(-1px);
}

.recent-trip-thumb {
  width: 44px;
  height: 44px;
  border-radius: var(--radius-md);
  overflow: hidden;
  flex-shrink: 0;
}

.recent-trip-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumb-placeholder {
  width: 100%;
  height: 100%;
  background: var(--color-primary-light);
  color: var(--color-primary-ink);
  display: flex;
  align-items: center;
  justify-content: center;
}

.recent-trip-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.recent-trip-title {
  font-size: 0.9375rem;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.recent-trip-meta {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.6875rem;
  color: var(--color-text-muted);
  flex-wrap: wrap;
}

.trip-status-tag {
  background: #f0fdf4;
  color: #166534;
  border: 1px solid #bbf7d0;
  padding: 0.05rem 0.35rem;
  border-radius: var(--radius-sm);
  font-weight: 700;
  font-size: 0.625rem;
}

.recent-trip-menu-icon {
  color: var(--color-text-muted);
  font-size: 1.1rem;
  padding: 0.25rem;
}

@media (max-width: 767px) {
  .hub-shell {
    gap: 1.25rem;
  }
  .intro-panel {
    padding: 1.25rem;
  }
  .intro-panel h1 {
    font-size: 1.45rem;
  }
  .action-grid {
    grid-template-columns: 1fr;
    gap: 0.65rem;
  }
  .action-panel {
    min-height: auto;
    padding: 1rem;
  }
  .action-arrow {
    transform: rotate(0deg);
  }
}
</style>
